import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { Plus, Play, ChevronDown, ChevronRight, RefreshCw, Eye, Search, X, Loader2, CheckCircle2, BarChart2 } from 'lucide-react';
import Toast, { ToastMessage } from '../../common/Toast';
import Button from '../../common/Button';
import Skeleton from '../../common/Skeleton';
import FormControl from '../../common/FormControl';
import Pagination from '../../common/Pagination';
import { confirmDelete } from '../../common/confirm';
import { Rule, RuleRun, DataSet } from '../types';
import { formatDate } from '../mockData';
import { operatorByKind } from '../operators';
import FileAnalyticsAPI from '../fileAnalyticsService';
import Modal from '../../common/Modal';
import RuleBuilderModal from '../modals/RuleBuilderModal';
import RuleRunModal from '../modals/RuleRunModal';
import DatasetDetailsModal from '../modals/DatasetDetailsModal';
import { useCanDefineRules } from '../permissions';
import { usePermission, NOT_AUTHORISED_MSG } from '../../../contexts/PermissionContext';

// Render a rule step as a plain-language phrase — no dataset filenames, no raw operator codes.
type StepLike = { column: string; column_type?: string; operator: string; params?: string[]; source_alias?: string };
const HL: React.CSSProperties = { color: 'var(--interactive-primary-bg)', fontWeight: 600 };

/**
 * Format a column label. In multi-source rules, prefix it with its source alias
 * (e.g. "bank · amount"). Single-source rules render the bare column name, matching
 * the previous behaviour byte-for-byte.
 */
function fmtCol(columnName: string, alias: string | undefined, multiSource: boolean): string {
  if (multiSource && alias) return `${alias} · ${columnName}`;
  return columnName;
}

/**
 * For col_* operators the compare param may be "alias.colname" (joined-rule form).
 * When multi-source, split on the first dot and render as "alias · colname".
 */
function fmtCompareCol(param: string, multiSource: boolean): string {
  if (multiSource && param.includes('.')) {
    const dot = param.indexOf('.');
    return `${param.slice(0, dot)} · ${param.slice(dot + 1)}`;
  }
  return param;
}

function describeStep(step: StepLike, multiSource = false): React.ReactNode {
  const p = step.params || [];
  const v = (i: number) => (p[i] ?? '').toString();
  const colLabel = fmtCol(step.column, step.source_alias, multiSource);
  const col = <strong>{colLabel}</strong>;
  const ref = (s: string) => <span style={HL}>{s}</span>;
  // For col_* operators: the compare param may be a qualified alias.col reference.
  const cref = (i: number) => ref(fmtCompareCol(v(i), multiSource));
  switch (step.operator) {
    case 'gt':  return <>{col} &gt; {ref(v(0))}</>;
    case 'gte': return <>{col} ≥ {ref(v(0))}</>;
    case 'lt':  return <>{col} &lt; {ref(v(0))}</>;
    case 'lte': return <>{col} ≤ {ref(v(0))}</>;
    case 'eq':  return <>{col} = {ref(v(0))}</>;
    case 'neq': return <>{col} ≠ {ref(v(0))}</>;
    case 'between': return <>{col} between {ref(v(0))} and {ref(v(1))}</>;
    case 'is_null': return <>{col} is empty</>;
    case 'is_not_null': return <>{col} is not empty</>;
    case 'contains': return <>{col} contains {ref(v(0))}</>;
    case 'starts_with': return <>{col} starts with {ref(v(0))}</>;
    case 'ends_with': return <>{col} ends with {ref(v(0))}</>;
    case 'regex': return <>{col} matches /{v(0)}/</>;
    case 'in_list': return <>{col} is one of {ref(v(0))}</>;
    case 'not_in_list': return <>{col} is not one of {ref(v(0))}</>;
    case 'within_last_days': return <>{col} within the last {ref(v(0))} days</>;
    case 'top_n': return <>top {ref(v(0))} rows by {col}</>;
    case 'bottom_n': return <>bottom {ref(v(0))} rows by {col}</>;
    case 'std_dev_above': return <>{col} is {ref(v(0))}+ std-dev above average</>;
    case 'std_dev_below': return <>{col} is {ref(v(0))}+ std-dev below average</>;
    case 'col_gt':  return <>{col} &gt; {cref(0)}</>;
    case 'col_gte': return <>{col} ≥ {cref(0)}</>;
    case 'col_lt':  return <>{col} &lt; {cref(0)}</>;
    case 'col_lte': return <>{col} ≤ {cref(0)}</>;
    case 'col_eq':  return <>{col} = {cref(0)}</>;
    case 'col_neq': return <>{col} ≠ {cref(0)}</>;
    case 'is_weekend': return <>{col} falls on a weekend</>;
    case 'is_nonworking_day': return <>{col} falls on a weekend{v(0) ? ' or declared holiday' : ''}</>;
    case 'hour_between': return <>{col} time between {ref(v(0))}:00 and {ref(v(1))}:00</>;
    case 'hour_not_between': return <>{col} time outside {ref(v(0))}:00–{ref(v(1))}:00</>;
    case 'date_diff_days_ge': return <>{col} is ≥ {ref(v(1))} days after {ref(v(0))}</>;
    case 'date_diff_days_le': return <>{col} is ≤ {ref(v(1))} days after {ref(v(0))}</>;
    case 'date_diff_days_gt': return <>{col} is more than {ref(v(1))} days after {ref(v(0))}</>;
    case 'date_diff_days_lt': return <>{col} is less than {ref(v(1))} days after {ref(v(0))}</>;
    case 'date_diff_years_between':
    case 'date_diff_years_not_between': {
      const parts = (v(0).includes(',') ? v(0).split(',') : p).map(s => (s ?? '').toString().trim());
      const word = step.operator === 'date_diff_years_not_between' ? 'outside' : 'within';
      return <>years from {ref(parts[0] ?? '')} to {col} {word} {ref(parts[1] ?? '')}–{ref(parts[2] ?? '')}</>;
    }
    case 'in_dataset': return <>{col} found in the reference pipeline</>;
    case 'not_in_dataset': return <>{col} not found in the reference pipeline</>;
    case 'lookup_date_diff_days_lt': return <>{col} matched record within {ref((p[4] ?? '').toString())} days</>;
    case 'group_count_gt': return <>duplicate group on {ref(v(0))} (more than {ref(v(1) || '1')} rows)</>;
    case 'bruteforce_then_success': return <>{ref((p[5] ?? '5').toString())}+ failures then a success per {ref(v(0))}</>;
    case 'repeated_distinct_in_window': return <>{ref(v(2))} changes within {ref(v(3))} min per {ref(v(0))}</>;
    default: {
      const op = operatorByKind(step.operator as any);
      const pd = p.filter(Boolean).join(', ');
      return <>{col} {op?.label ?? step.operator}{pd ? <> {ref(pd)}</> : null}</>;
    }
  }
}

const PREVIEW_PAGE_SIZE = 50;

interface PreviewState {
  columns: string[];
  rows: any[][];
  total: number;
  page: number;
  search: string;
}

/** Imperative handle so a parent toolbar (CM Rule Governance) can trigger "New Rule"
 *  from next to its own "Edit Pipeline" button while this section owns builder state. */
export interface RulesSectionHandle {
  openCreate: () => void;
}

interface RulesSectionProps {
  collectionTypeId?: string;
  /** Name of the pipeline/source — forwarded to the rule builder heading. */
  collectionName?: string;
  mode?: 'define' | 'run';
  hideRun?: boolean;
  /** Suppress the inline "New Rule" toolbar button when the parent renders its own. */
  hideCreate?: boolean;
  /**
   * When set, clicking a rule row (define mode) fires this instead of opening the
   * rule builder. Used by the DA read-only pipeline popup to open a rule's results.
   */
  onRuleClick?: (rule: Rule) => void;
  /** Pipeline is running — define-mode rows show a live spinner. */
  running?: boolean;
  /** Pipeline run just finished — define-mode rows show a done tick. */
  done?: boolean;
}

const RulesSection = forwardRef<RulesSectionHandle, RulesSectionProps>(({ collectionTypeId, collectionName, mode = 'define', hideRun = false, hideCreate = false, onRuleClick, running = false, done = false }, ref) => {
  const canDefine = useCanDefineRules();
  const { allowed: canModify, loading: permLoading } = usePermission('CMG_RULES', 'view+modify');
  const showCrud  = mode === 'define' && canDefine;
  const [rules, setRules]               = useState<Rule[]>([]);
  const [loading, setLoading]           = useState(false);
  const [showBuilder, setShowBuilder]   = useState(false);
  const [editingRule, setEditingRule]   = useState<Rule | null>(null);
  const [expandedId, setExpandedId]     = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Rule['status'] | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<Rule['severity'] | 'all'>('all');
  const [search, setSearch]             = useState('');
  const [toast, setToast]               = useState<ToastMessage | null>(null);
  const [runningId, setRunningId]       = useState<string | null>(null);
  const [runResults, setRunResults]     = useState<Record<string, RuleRun>>({});
  const [previewDataset, setPreviewDataset] = useState<DataSet | null>(null);
  const [rulePreview, setRulePreview]   = useState<Record<string, PreviewState>>({});
  const [rulePreviewLoading, setRulePreviewLoading] = useState<Record<string, boolean>>({});
  const [ruleSearchTerms, setRuleSearchTerms] = useState<Record<string, string>>({});
  const [page, setPage]                 = useState(1);
  const [pageSize, setPageSize]         = useState(10);
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useImperativeHandle(ref, () => ({
    openCreate: () => { setEditingRule(null); setShowBuilder(true); },
  }));

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await FileAnalyticsAPI.listRules(collectionTypeId);
      setRules(list);
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to load rules' });
    } finally {
      setLoading(false);
    }
  }, [collectionTypeId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setPage(1); }, [statusFilter, severityFilter, search, pageSize]);

  // When the pipeline is running, poll so per-rule last_* counts update live and
  // flip from Running → result the moment the run completes.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(refresh, 1500);
    return () => clearInterval(t);
  }, [running, refresh]);

  const filteredRules = useMemo(() => rules.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (severityFilter !== 'all' && r.severity !== severityFilter) return false;
    if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rules, statusFilter, severityFilter, search]);

  const paginatedRules = filteredRules.slice((page - 1) * pageSize, page * pageSize);

  // Running happens via the focused Run popup (lists rules, shows per-rule progress,
  // offers per-rule download). Only mounted when this section is run-capable (!hideRun).
  const [runPopupOpen, setRunPopupOpen] = useState(false);

  // "Send to Data Analytics" — per-rule loading state + name-prompt modal
  const [sendingRunId, setSendingRunId] = useState<string | null>(null);
  const [analyticsPrompt, setAnalyticsPrompt] = useState<{ ruleId: string; runId: number | string; defaultName: string } | null>(null);
  const [analyticsName, setAnalyticsName] = useState('');
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const handleSendToAnalytics = async (ruleId: string, runId: number | string, name: string) => {
    setSendingRunId(ruleId);
    setAnalyticsError(null);
    try {
      const result = await FileAnalyticsAPI.sendRunToAnalytics(runId, name.trim() || undefined);
      setAnalyticsPrompt(null);
      setToast({ type: 'success', message: `Sent to Data Analytics as "${result.data.title}"` });
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to send to Data Analytics';
      if (err?.response?.status === 409) {
        setAnalyticsError(msg);
      } else {
        setAnalyticsPrompt(null);
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setSendingRunId(null);
    }
  };

  const handleRun = async (id: string) => {
    setRunningId(id);
    setExpandedId(id);
    try {
      const result: any = await FileAnalyticsAPI.runRule(id);
      if (result && typeof result === 'object') {
        setRunResults(prev => ({ ...prev, [id]: result as RuleRun }));
        if (result.output_dataset_id) {
          setRulePreview(prev => { const n = { ...prev }; delete n[id]; return n; });
          loadRulePreviewPage(id, result.output_dataset_id, 0);
        }
      }
      const rowsMsg = result?.rows_matched != null ? `${result.rows_matched} rows matched` : 'completed';
      setToast({ type: 'success', message: `Rule ${rowsMsg}` });
      await refresh();
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Run failed' });
    } finally {
      setRunningId(null);
    }
  };

  const handleDelete = async (id: string): Promise<boolean> => {
    const ok = await confirmDelete({ text: 'This rule will be permanently removed.' });
    if (!ok) return false;
    try {
      await FileAnalyticsAPI.deleteRule(id);
      setToast({ type: 'delete', message: 'Rule deleted' });
      await refresh();
      return true;
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Delete failed' });
      return false;
    }
  };

  const loadRulePreviewPage = async (ruleId: string, datasetId: string, page: number, search = '') => {
    setRulePreviewLoading(prev => ({ ...prev, [ruleId]: true }));
    try {
      const data = await FileAnalyticsAPI.previewDataset(datasetId, PREVIEW_PAGE_SIZE, page * PREVIEW_PAGE_SIZE, search || undefined);
      setRulePreview(prev => ({ ...prev, [ruleId]: { ...data, page, search } }));
    } catch {
      setToast({ type: 'error', message: 'Failed to load rule result preview' });
    } finally {
      setRulePreviewLoading(prev => ({ ...prev, [ruleId]: false }));
    }
  };

  const handleRuleSearch = (ruleId: string, datasetId: string, term: string) => {
    setRuleSearchTerms(prev => ({ ...prev, [ruleId]: term }));
    clearTimeout(debounceRef.current[ruleId]);
    debounceRef.current[ruleId] = setTimeout(() => {
      loadRulePreviewPage(ruleId, datasetId, 0, term);
    }, 400);
  };

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* ── Toolbar — run side only; the define side puts New Rule in the panel header. ── */}
      {mode !== 'define' && (
        <div className="netbody-header">
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Detection Rules</span>
          <div className="flex items-center gap-2 ml-auto">
            {!hideRun && rules.length > 0 && (
              <Button
                variant="fill"
                icon={Play}
                onClick={() => setRunPopupOpen(true)}
                data-testid="BTN_fa_rules_run_all"
              >
                Run Rules
              </Button>
            )}
            <FormControl.SearchSelect
              value={statusFilter}
              onChange={v => setStatusFilter((v ?? 'all') as typeof statusFilter)}
              className="filter-status"
              data-testid="SEL_fa_rules_status"
              noSearchIcon
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'Active', label: 'Active' },
                { value: 'Draft', label: 'Draft' },
                { value: 'Paused', label: 'Paused' },
                { value: 'Failed', label: 'Failed' },
              ]}
            />
            <FormControl.SearchSelect
              value={severityFilter}
              onChange={v => setSeverityFilter((v ?? 'all') as typeof severityFilter)}
              className="filter-status"
              data-testid="SEL_fa_rules_severity"
              noSearchIcon
              options={[
                { value: 'all', label: 'All severity' },
                { value: 'Critical', label: 'Critical' },
                { value: 'High', label: 'High' },
                { value: 'Medium', label: 'Medium' },
                { value: 'Low', label: 'Low' },
              ]}
            />
            <FormControl.Input
              type="text"
              placeholder="Search rules…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_fa_rules_search"
            />
            <Button variant="secondary-outline" onClick={refresh} disabled={loading} title="Refresh" data-testid="BTN_fa_rules_refresh">
              <RefreshCw size={14} className={loading ? 'spin' : ''} />
            </Button>
          </div>
        </div>
      )}

      {/* ── Rules list — define sits flush inside the panel; run uses the full-height scroll. ── */}
      <div className={mode === 'define' ? '' : 'netbody-content'}>
        <div className={mode === 'define' ? '' : 'scroll-container'}>
          {loading ? (
            onRuleClick ? (
              /* flat-list skeleton — matches actual define+onRuleClick row layout */
              <div style={{ paddingTop: 4 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="fa-skel-row">
                    <Skeleton.Box width={22} height={12} />
                    <Skeleton.Box width={`${45 + (i * 11) % 35}%`} height={13} />
                    <Skeleton.Box width={24} height={13} style={{ marginLeft: 'auto' }} />
                  </div>
                ))}
              </div>
            ) : (
            <div className="table-list" style={{ paddingTop: 4 }}>
              <div className="table-list-scroll">
                <table className="table-list-table" aria-busy="true">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Name</th>
                      <th className="table-list-th">Severity</th>
                      <th className="table-list-th">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <Skeleton.TableRows cols={['3xs', 'md', 'pill', 'pill']} rows={5} />
                  </tbody>
                </table>
              </div>
            </div>
            )
          ) : filteredRules.length === 0 ? (
            <div className="empty-state" style={{ padding: 48 }}>
              <div className="empty-state-content">
                <h3>No detection rules found</h3>
                <p>Click "+" to create your first detection rule.</p>
              </div>
            </div>
          ) : mode === 'define' ? (
            /* Define side: a clean listing (rows divided by a line) — click a row to open the rule popup. */
            <div style={{ paddingTop: 4 }}>
              {paginatedRules.map((r, idx) => (
                <div
                  key={r.id}
                  onClick={() => {
                    // Results-view caller: open the rule's results, skip edit + perm gate.
                    // While the pipeline runs, rows are inert (no stale results mid-run).
                    if (onRuleClick) { if (!running) onRuleClick(r); return; }
                    if (permLoading) return;
                    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
                    setEditingRule(r); setShowBuilder(true);
                  }}
                  title={onRuleClick ? (running ? 'Running…' : 'View results') : (!canModify ? NOT_AUTHORISED_MSG : undefined)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 6px', cursor: (onRuleClick && running) ? 'default' : (!onRuleClick && (permLoading || !canModify)) ? 'not-allowed' : 'pointer', borderRadius: 6, opacity: (!onRuleClick && (permLoading || !canModify)) ? 0.55 : 1 }}
                  data-testid={`BTN_fa_rule_row_${r.id}`}
                >
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)', minWidth: 22, textAlign: 'right' }}>{(page - 1) * pageSize + idx + 1}.</span>
                  {/* Lifecycle icons (leading), same as the Pipelines column. */}
                  {onRuleClick && running && (
                    <Loader2 size={14} className="spin fa-stateicon fa-stateicon--info" />
                  )}
                  {onRuleClick && !running && done && (
                    <CheckCircle2 size={14} className="fa-stateicon fa-stateicon--ok" />
                  )}
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  {onRuleClick && !running && (
                    <>
                      {/* Results-view caller: latest anomaly count per rule, plain grey number. */}
                      {(() => {
                        const hits = r.last_rows_matched ?? 0;
                        const ran  = r.last_output_dataset_id != null || r.last_run_at != null;
                        return (
                          <span
                            style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}
                            title={ran ? `${hits} anomalies in the latest run` : 'Not run yet'}
                          >
                            {ran ? hits.toLocaleString() : '—'}
                          </span>
                        );
                      })()}
                      {/* Affordance: rule name opens its results in this (read-only) caller. */}
                      <ChevronRight size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden="true" />
                    </>
                  )}
                </div>
              ))}
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={filteredRules.length}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                recordLabel="rules"
              />
            </div>
          ) : (
            <>
              <div className="collapse-section-stack" style={{ paddingTop: 12 }}>
                {paginatedRules.map(r => {
                  const isOpen = expandedId === r.id;
                  return (
                    <div key={r.id} className={`collapse-section collapse-section--l1 ${isOpen ? 'cs-open' : 'cs-closed'}`}>
                      <div
                        className="collapse-section__header"
                        onClick={() => {
                          const opening = expandedId !== r.id;
                          setExpandedId(opening ? r.id : null);
                          if (opening && !rulePreview[r.id] && r.last_output_dataset_id) {
                            loadRulePreviewPage(r.id, r.last_output_dataset_id, 0);
                          }
                        }}
                        data-testid={`BTN_fa_rule_toggle_${r.id}`}
                      >
                        <span className="collapse-section__toggle"><ChevronDown size={14} /></span>
                        <div className="collapse-section__title-block">
                          <span className="collapse-section__name">{r.name}</span>
                        </div>
                        <div className="collapse-section__meta" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {r.tags.map(t => <span key={t} className="fa-tag">{t}</span>)}
                          {r.risk_code
                            ? <span className="fa-tag" data-testid={`rule-risk-${r.id}`} title={r.risk_name}>{r.risk_code}</span>
                            : <span className="fa-tag" style={{ opacity: 0.6 }} data-testid={`rule-risk-${r.id}`}>Unlinked</span>}
                          <span className="collapse-section__score-label">{r.severity}</span>
                          <span className="collapse-section__score-label">{r.steps.length} {r.steps.length === 1 ? 'step' : 'steps'}</span>
                          <span className="collapse-section__score-label" style={{ whiteSpace: 'nowrap' }}>{formatDate(r.last_run_at)}</span>
                          <span className="collapse-section__score-label">{r.status}</span>
                          {/* Run only on the run side. Edit/Delete live in the popup (define side). */}
                          {!hideRun && (
                            <div style={{ display: 'flex', gap: 2, marginLeft: 4 }} onClick={e => e.stopPropagation()}>
                              <button
                                type="button"
                                className="tree-list-action"
                                aria-label="Run"
                                title="Run now"
                                onClick={() => handleRun(r.id)}
                                disabled={runningId === r.id}
                                data-testid={`BTN_fa_rule_run_${r.id}`}
                              >
                                {runningId === r.id ? '…' : <Play size={14} />}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {isOpen && (() => {
                        const run = runResults[r.id];
                        const pv = rulePreview[r.id];
                        const isLoadingPv = !!rulePreviewLoading[r.id];
                        const outputDatasetId = run?.output_dataset_id ?? r.last_output_dataset_id ?? null;
                        const runId = run?.id ?? r.last_run_id ?? null;
                        const rowsMatched = run?.rows_matched ?? r.last_rows_matched;
                        const rowsIn = run?.rows_in ?? r.last_rows_in;

                        return (
                          <div className="collapse-section__body">
                            {/* Run result banner */}
                            {(rowsMatched != null || r.last_run_at) && outputDatasetId && (
                              <div style={{
                                padding: '10px 14px', borderRadius: 6, fontSize: 12,
                                background: (rowsMatched ?? 0) > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                                border: `1px solid ${(rowsMatched ?? 0) > 0 ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.25)'}`,
                                color: (rowsMatched ?? 0) > 0 ? 'rgb(185,28,28)' : 'rgb(21,128,61)',
                                display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
                              }}>
                                <span>
                                  Last run — <strong>{(rowsMatched ?? 0).toLocaleString()}</strong> anomalous rows
                                  {rowsIn != null && <> out of <strong>{rowsIn.toLocaleString()}</strong> total</>}
                                </span>
                                {!pv && !isLoadingPv && (
                                  <Button
                                    variant="secondary-outline"
                                    icon={Eye}
                                    onClick={() => loadRulePreviewPage(r.id, outputDatasetId, 0)}
                                  >
                                    Load matched rows
                                  </Button>
                                )}
                                {runId && (
                                  <Button
                                    variant="secondary-outline"
                                    icon={sendingRunId === r.id ? Loader2 : BarChart2}
                                    disabled={sendingRunId === r.id}
                                    onClick={() => {
                                      setAnalyticsName(r.name);
                                      setAnalyticsError(null);
                                      setAnalyticsPrompt({ ruleId: r.id, runId, defaultName: r.name });
                                    }}
                                  >
                                    {sendingRunId === r.id ? 'Sending…' : 'Send to Data Analytics'}
                                  </Button>
                                )}
                              </div>
                            )}

                            {/* Steps */}
                            <div className="fa-rule-steps">
                              {r.steps.map((step, idx) => (
                                  <React.Fragment key={step.id}>
                                    <div className="fa-rule-step">
                                      <span className="fa-rule-step-number">{step.step_number}</span>
                                      <div className="fa-rule-step-summary">
                                        <div className="fa-rule-step-title" style={{ fontSize: 13 }}>
                                          {describeStep(step, r.sources.length > 1)}
                                        </div>
                                        {step.description && (
                                          <div className="fa-rule-step-desc">{step.description}</div>
                                        )}
                                      </div>
                                    </div>
                                    {idx < r.steps.length - 1 && (
                                      <div className="fa-rule-step-chain">↓ then</div>
                                    )}
                                  </React.Fragment>
                              ))}
                            </div>
                            <div className="fa-help">
                              Created {formatDate(r.created_at)}.
                            </div>

                            {/* Inline matched-rows preview */}
                            {outputDatasetId && (isLoadingPv || pv) && (
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: 'var(--text-primary)' }}>
                                  Matched Rows
                                </div>
                                {isLoadingPv ? (
                                  <div className="collapse-section__skeleton">
                                    <Skeleton.Box height={12} width="60%" />
                                    <Skeleton.Box height={12} width="80%" />
                                    <Skeleton.Box height={12} width="45%" />
                                  </div>
                                ) : pv && (
                                  <div>
                                    {/* Preview search + info */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                                      <div style={{ position: 'relative', width: 260 }}>
                                        <FormControl.Input
                                          type="text"
                                          placeholder="Search all columns…"
                                          value={ruleSearchTerms[r.id] ?? ''}
                                          onChange={e => handleRuleSearch(r.id, outputDatasetId, e.target.value)}
                                          leadingIcon={<Search size={13} />}
                                          style={{ fontSize: 12, paddingRight: ruleSearchTerms[r.id] ? 28 : undefined }}
                                          data-testid={`INP_fa_rule_preview_search_${r.id}`}
                                        />
                                        {ruleSearchTerms[r.id] && (
                                          <button
                                            type="button"
                                            onClick={() => handleRuleSearch(r.id, outputDatasetId, '')}
                                            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 0, cursor: 'pointer', padding: 0, color: 'var(--text-muted)' }}
                                          >
                                            <X size={12} />
                                          </button>
                                        )}
                                      </div>
                                      {pv.search && (
                                        <span style={{ fontSize: 12, color: 'var(--interactive-primary-bg)' }}>filtered</span>
                                      )}
                                    </div>

                                    {/* Preview data table */}
                                    <div style={{ overflowX: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                                      <table className="table-list-table" style={{ fontSize: 12, minWidth: 'max-content', width: '100%' }}>
                                        <thead>
                                          <tr>
                                            <th className="table-list-th table-list-cell--sno">S.No</th>
                                            {pv.columns.map(col => (
                                              <th key={col} className="table-list-th" style={{ whiteSpace: 'nowrap' }}>{col}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {pv.rows.length === 0 ? (
                                            <tr>
                                              <td colSpan={pv.columns.length + 1} className="table-list-empty-cell">
                                                No rows match.
                                              </td>
                                            </tr>
                                          ) : pv.rows.map((row, ri) => (
                                            <tr key={ri} className="table-list-row">
                                              <td className="table-list-cell" style={{ textAlign: 'right', paddingRight: 12, color: 'var(--text-tertiary)', fontSize: 11 }}>
                                                {pv.page * PREVIEW_PAGE_SIZE + ri + 1}
                                              </td>
                                              {row.map((cell, ci) => (
                                                <td key={ci} className="table-list-cell" style={{ whiteSpace: 'nowrap' }}>
                                                  {typeof cell === 'number' ? cell.toLocaleString() : String(cell ?? '')}
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    {/* Preview pagination */}
                                    <Pagination
                                      page={pv.page + 1}
                                      pageSize={PREVIEW_PAGE_SIZE}
                                      totalRecords={pv.total}
                                      onPageChange={(newPage) => loadRulePreviewPage(r.id, outputDatasetId, newPage - 1, pv.search)}
                                      recordLabel="rows"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={filteredRules.length}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                recordLabel="rules"
              />
            </>
          )}
        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────────────────── */}
      <RuleBuilderModal
        isOpen={showBuilder}
        mode={mode}
        rule={editingRule}
        onClose={() => { setShowBuilder(false); setEditingRule(null); }}
        collectionTypeId={collectionTypeId}
        collectionName={collectionName}
        onDelete={editingRule ? async () => {
          const deleted = await handleDelete(editingRule.id);
          if (deleted) { setShowBuilder(false); setEditingRule(null); }
        } : undefined}
        onSaved={async () => {
          setShowBuilder(false);
          setEditingRule(null);
          setToast({ type: 'success', message: 'Rule saved' });
          await refresh();
        }}
      />
      <DatasetDetailsModal
        isOpen={!!previewDataset}
        onClose={() => setPreviewDataset(null)}
        dataset={previewDataset}
      />
      {!hideRun && (
        <RuleRunModal
          isOpen={runPopupOpen}
          onClose={() => { setRunPopupOpen(false); refresh(); }}
          collectionTypeId={collectionTypeId}
        />
      )}

      {/* Send to Data Analytics — name prompt */}
      {analyticsPrompt && (
        <Modal isOpen onClose={() => setAnalyticsPrompt(null)} size="sm">
          <Modal.Header>Send to Data Analytics</Modal.Header>
          <Modal.Body>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-heading)', marginBottom: 6 }}>
              Data set name
            </label>
            <input
              type="text"
              value={analyticsName}
              autoFocus
              onChange={e => { setAnalyticsName(e.target.value); setAnalyticsError(null); }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !sendingRunId)
                  handleSendToAnalytics(analyticsPrompt.ruleId, analyticsPrompt.runId, analyticsName);
              }}
              placeholder="Enter a name for the data set"
              style={{ width: '100%', padding: '9px 11px', fontSize: 13, border: `1px solid ${analyticsError ? 'var(--status-danger-border, #f87171)' : 'var(--border-subtle)'}`, borderRadius: 6, boxSizing: 'border-box' }}
            />
            {analyticsError && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--status-danger-text, #b91c1c)' }}>{analyticsError}</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={() => setAnalyticsPrompt(null)} disabled={!!sendingRunId}>Cancel</Button>
            <Button
              variant="fill"
              icon={sendingRunId ? Loader2 : BarChart2}
              onClick={() => handleSendToAnalytics(analyticsPrompt.ruleId, analyticsPrompt.runId, analyticsName)}
              disabled={!!sendingRunId || !analyticsName.trim()}
            >
              {sendingRunId ? 'Sending…' : 'Add to Data Analytics'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
});

RulesSection.displayName = 'RulesSection';

export default RulesSection;
